import React, { useEffect, useState } from "react";
import { marketingApi, fmtUsd, fmtPct, fmtInt } from "../marketingApi";
import { Section, EmptyState, Loading, Kpi, Tip } from "../components";

const Insufficient = ({ label = "Datos insuficientes" }) => (
  <span className="text-amber-600 text-xs font-medium">{label}</span>
);

export default function ProfitabilityTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    marketingApi.profitability().then(setData).catch((e) => setErr(e?.response?.data?.detail || "Error"));
  }, []);
  if (err) return <EmptyState title="Error al cargar" description={err} testId="profit-error" />;
  if (!data) return <Loading />;
  const rows = data.rows || [];
  const t = data.totals || {};
  return (
    <div className="space-y-6" data-testid="profit-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testId="profit-kpi-tours" label="Expediciones" value={fmtInt(t.tours_count)} />
        <Kpi testId="profit-kpi-rev" label="Ingresos previstos" value={fmtUsd(t.revenue_total)} />
        <Kpi testId="profit-kpi-margin" label="Margen bruto previsto" value={fmtUsd(t.margin_total)} tone="primary" />
        <Kpi testId="profit-kpi-leads" label="Leads atribuidos" value={fmtInt(t.leads_attributed_total)} hint="Leads cuyo email coincide con un participante" />
      </div>

      <Section
        title="Rentabilidad por expedición"
        subtitle="Mismos cálculos que la pestaña Gastos & Rentabilidad del tour. Hover sobre cada columna para ver la fórmula."
      >
        {rows.length === 0 ? (
          <EmptyState title="No hay tours disponibles" description="Crea tus primeras expediciones para ver rentabilidad." />
        ) : (
          <div className="overflow-x-auto -mx-5 sm:mx-0">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase tracking-wider">
                <tr className="border-b border-slate-200 dark:border-[#25334D]">
                  <th className="py-2 px-3 text-left">Expedición</th>
                  <th className="py-2 px-3 text-left">País</th>
                  <th className="py-2 px-3 text-right" title="Participantes confirmados (deposit_paid, confirmed, partial_paid, fully_paid)">PAX</th>
                  <th className="py-2 px-3 text-right" title="Σ (precio + supl. − descuento − comisión) por participante confirmado">Ingresos</th>
                  <th className="py-2 px-3 text-right" title="Σ gastos globales + per_person×pax + hoteles + transportes + vuelos (sin pendientes)">Gastos</th>
                  <th className="py-2 px-3 text-right" title="Ingresos − Gastos">Margen</th>
                  <th className="py-2 px-3 text-right" title="Margen / Ingresos × 100">%</th>
                  <th className="py-2 px-3 text-right">Margen/cliente</th>
                  <th className="py-2 px-3 text-right">Leads</th>
                  <th className="py-2 px-3 text-right">Ad spend</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.tour_id} className="border-b border-slate-100 dark:border-[#1A2336]">
                    <td className="py-2 px-3 font-medium">{r.tour_name || "—"}</td>
                    <td className="py-2 px-3 text-slate-500">{r.country || "—"}</td>
                    <td className="py-2 px-3 text-right">{fmtInt(r.pax)}</td>
                    <td className="py-2 px-3 text-right">{r.insufficient_revenue_data ? <Insufficient /> : fmtUsd(r.expected_revenue)}</td>
                    <td className="py-2 px-3 text-right">{fmtUsd(r.budgeted_expenses)}</td>
                    <td className="py-2 px-3 text-right font-semibold">{r.gross_margin_expected === null ? <Insufficient /> : fmtUsd(r.gross_margin_expected)}</td>
                    <td className="py-2 px-3 text-right text-slate-500">{fmtPct(r.margin_pct)}</td>
                    <td className="py-2 px-3 text-right text-slate-500">{r.profit_per_client === null ? "—" : fmtUsd(r.profit_per_client)}</td>
                    <td className="py-2 px-3 text-right">{fmtInt(r.leads_attributed)}</td>
                    <td className="py-2 px-3 text-right">{r.insufficient_cost_data ? <Insufficient label="No conectado" /> : fmtUsd(r.marketing_cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4 text-[11px] text-slate-500 space-y-1">
          <div><Tip>Ingresos: Σ (precio + supl. − descuento − comisión) de participantes con estado deposit_paid/confirmed/partial_paid/fully_paid.</Tip></div>
          <div><Tip>Gastos: globales + per_person×pax + hoteles(precio×hab×noches) + transportes + vuelos reservados.</Tip></div>
          <div><Tip>Ad spend aparece como «No conectado» hasta que configures Google Ads o Meta Ads en Integraciones.</Tip></div>
        </div>
      </Section>
    </div>
  );
}
